import React, { useState, useEffect, useRef } from 'react';
import { ArrowRightLeft, Upload, FileAudio, X, Loader2, Headphones } from 'lucide-react';
import { convertSTS, getVoices, getModels } from '../../services/speechApi';
import { toast } from '../ui/Toast';
import SpeechAudioPlayer from './SpeechAudioPlayer';

const MAX_FILE_SIZE = 25 * 1024 * 1024;
const ACCEPTED_FORMATS = '.mp3,.wav,.m4a,.webm,.ogg,.flac';

const SpeechSTS = () => {
  const [stsModels, setStsModels] = useState([]);
  const [model, setModel] = useState('eleven_english_sts_v2');
  const [voices, setVoices] = useState([]);
  const [voice, setVoice] = useState('');
  const [removeNoise, setRemoveNoise] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingVoices, setLoadingVoices] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const fileInputRef = useRef(null);
  const dropRef = useRef(null);

  // Charger modèles STS + voix ElevenLabs
  useEffect(() => {
    getModels().then(data => setStsModels(data.sts || [])).catch(() => {});
    setLoadingVoices(true);
    getVoices('elevenlabs')
      .then(v => { setVoices(v); if (v.length > 0) setVoice(v[0].id); })
      .catch(() => setVoices([]))
      .finally(() => setLoadingVoices(false));
  }, []);

  const handleFileSelect = (file) => {
    if (!file) return;
    if (file.size > MAX_FILE_SIZE) {
      toast.error(`Fichier trop volumineux (max ${MAX_FILE_SIZE / 1024 / 1024} Mo)`);
      return;
    }
    setUploadFile(file);
    setAudioUrl(null);
  };

  const handleConvert = async () => {
    if (!uploadFile || !voice) return;
    setLoading(true);
    setAudioUrl(null);
    try {
      const url = await convertSTS(uploadFile, voice, model, removeNoise);
      setAudioUrl(url);
      toast.success('Conversion terminée');
    } catch (err) {
      toast.error(err.message || 'Erreur conversion vocale');
    } finally {
      setLoading(false);
    }
  };

  const handleDragOver = (e) => { e.preventDefault(); dropRef.current.style.borderColor = '#3b82f6'; };
  const handleDragLeave = (e) => { e.preventDefault(); dropRef.current.style.borderColor = 'var(--card-border)'; };
  const handleDrop = (e) => {
    e.preventDefault();
    dropRef.current.style.borderColor = 'var(--card-border)';
    if (e.dataTransfer.files?.[0]) handleFileSelect(e.dataTransfer.files[0]);
  };

  const formatFileSize = (bytes) => {
    if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} Mo`;
    return `${(bytes / 1024).toFixed(0)} Ko`;
  };

  return (
    <div style={{ display: 'flex', gap: '24px', height: '100%', padding: '24px 0' }}>

      {/* Colonne gauche — Configuration */}
      <div style={{ flex: '0 0 400px', display: 'flex', flexDirection: 'column', gap: '20px' }}>

        {/* Upload audio source */}
        <Field label="Audio source">
          <div ref={dropRef}
            onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            style={{
              background: 'var(--card-bg)', border: '2px dashed var(--card-border)',
              borderRadius: '14px', padding: '28px 20px', textAlign: 'center',
              cursor: 'pointer', transition: 'border-color 0.2s',
            }}>
            <FileAudio size={28} style={{ color: 'var(--text-secondary)', marginBottom: '8px' }} />
            <div style={{ fontSize: '13px', color: 'var(--text)', fontWeight: 500 }}>
              Glissez un fichier audio ici
            </div>
            <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '4px' }}>
              MP3, WAV, M4A, WebM (max 25 Mo)
            </div>
            <input ref={fileInputRef} type="file" accept={ACCEPTED_FORMATS} hidden
              onChange={e => handleFileSelect(e.target.files?.[0])} />
          </div>

          {uploadFile && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: '10px', marginTop: '10px',
              background: 'var(--card-bg)', border: '1px solid var(--card-border)',
              borderRadius: '10px', padding: '10px 14px',
            }}>
              <FileAudio size={16} style={{ color: '#3b82f6', flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '12px', color: 'var(--text)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {uploadFile.name}
                </div>
                <div style={{ fontSize: '10px', color: 'var(--text-secondary)' }}>{formatFileSize(uploadFile.size)}</div>
              </div>
              <button onClick={() => { setUploadFile(null); setAudioUrl(null); }}
                style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', padding: '4px' }}>
                <X size={14} />
              </button>
            </div>
          )}
        </Field>

        {/* Modèle STS */}
        <Field label="Modèle">
          <select value={model} onChange={e => setModel(e.target.value)} style={selectStyle}>
            {stsModels.map(m => (
              <option key={m.id} value={m.id}>{m.name}</option>
            ))}
          </select>
          {stsModels.find(m => m.id === model)?.description && (
            <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '4px' }}>
              {stsModels.find(m => m.id === model).description}
            </div>
          )}
        </Field>

        {/* Voix cible */}
        <Field label="Voix cible">
          {loadingVoices ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--text-secondary)', fontSize: '13px' }}>
              <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> Chargement...
            </div>
          ) : (
            <select value={voice} onChange={e => setVoice(e.target.value)} style={selectStyle}>
              {voices.map(v => (
                <option key={v.id} value={v.id}>
                  {v.name}{v.labels?.gender ? ` (${v.labels.gender})` : ''}
                </option>
              ))}
            </select>
          )}
        </Field>

        {/* Toggle bruit de fond */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <label style={{
            position: 'relative', width: '40px', height: '22px', cursor: 'pointer',
          }}>
            <input type="checkbox" checked={removeNoise} onChange={e => setRemoveNoise(e.target.checked)}
              style={{ opacity: 0, width: 0, height: 0 }} />
            <span style={{
              position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
              borderRadius: '11px', transition: 'background 0.2s',
              background: removeNoise ? '#1D4ED8' : 'var(--card-border)',
            }}>
              <span style={{
                position: 'absolute', top: '2px', left: removeNoise ? '20px' : '2px',
                width: '18px', height: '18px', borderRadius: '50%', background: '#fff',
                transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
              }} />
            </span>
          </label>
          <span style={{ fontSize: '13px', color: 'var(--text)' }}>Supprimer le bruit de fond</span>
        </div>

        {/* Bouton Convertir */}
        <button onClick={handleConvert} disabled={loading || !uploadFile || !voice}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
            padding: '14px 24px', borderRadius: '12px', border: 'none',
            cursor: loading || !uploadFile ? 'not-allowed' : 'pointer',
            background: loading || !uploadFile
              ? 'var(--card-border)'
              : 'linear-gradient(135deg, #1D4ED8 0%, #2563eb 100%)',
            color: '#fff', fontWeight: 600, fontSize: '15px', width: '100%',
            boxShadow: loading || !uploadFile ? 'none' : '0 4px 18px rgba(29,78,216,0.4)',
            transition: 'all 0.2s',
          }}>
          {loading ? (
            <><Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} /> Conversion en cours...</>
          ) : (
            <><ArrowRightLeft size={18} /> Convertir</>
          )}
        </button>
      </div>

      {/* Colonne droite — Résultat */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        {audioUrl ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text)', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Headphones size={16} style={{ color: '#3b82f6' }} />
              Audio converti
            </div>
            <SpeechAudioPlayer blobUrl={audioUrl} filename="itriia-sts-conversion.mp3" />
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
              ElevenLabs — {stsModels.find(m => m.id === model)?.name || model} — {voices.find(v => v.id === voice)?.name || voice}
              {removeNoise && ' — Bruit supprimé'}
            </div>
          </div>
        ) : loading ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: '16px' }}>
            <Loader2 size={36} style={{ color: '#3b82f6', animation: 'spin 1s linear infinite' }} />
            <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>Conversion en cours...</div>
          </div>
        ) : (
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            height: '100%', opacity: 0.4, gap: '12px',
          }}>
            <ArrowRightLeft size={48} style={{ color: 'var(--text-secondary)' }} />
            <div style={{ fontSize: '14px', color: 'var(--text-secondary)', textAlign: 'center', maxWidth: '300px' }}>
              Importez un fichier audio et choisissez une voix cible pour la conversion
            </div>
          </div>
        )}
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div>
    <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: 'var(--text)', marginBottom: '6px' }}>
      {label}
    </label>
    {children}
  </div>
);

const selectStyle = {
  width: '100%', padding: '10px 14px', borderRadius: '10px',
  border: '1px solid var(--card-border)', background: 'var(--input-bg)',
  color: 'var(--text)', fontSize: '14px', outline: 'none',
  boxSizing: 'border-box', cursor: 'pointer', appearance: 'auto',
};

export default SpeechSTS;
